/**
 * PLATINUM — Cart drawer upsell (v1)
 *
 * Replaces the Wisepops cart drawer cross-sell embed with a deterministic,
 * master-file-driven recommendation block.
 *
 * DATA
 *   assets/platinum-upsell-map.json — generated from platinum-products-master.md
 *   by scripts/build_upsell_map.js. Format: { sourceHandle: [{handle, reason, pitch?}] }
 *   Regenerate + re-upload when cross-sells in the master change.
 *
 * BEHAVIOR
 *   - Renders up to MAX_CARDS recommendation cards at the end of the cart
 *     drawer items list (same slot the Wisepops embed used).
 *   - Candidates: for each cart line (in order), its mapped targets (in order),
 *     deduped, excluding products already in cart and unavailable products.
 *   - "Dodaj" replicates the theme's native add flow (bundled sections +
 *     cart:change dispatch) so drawer, free shipping bar and header count
 *     all update natively without reload.
 *   - Re-renders on cart:change; re-injects after the drawer replaces its
 *     content (cart-drawer:refreshed) since that wipes our container.
 *
 * CONFIG injected by snippets/platinum-cart-upsell.liquid via
 * window.PlatinumUpsellConfig = { mapUrl, heading, maxCards }
 */

(function () {
  'use strict';

  const CFG = window.PlatinumUpsellConfig || {};
  const MAP_URL = CFG.mapUrl;
  const HEADING = CFG.heading || 'Dopolni svojo izbiro';
  const MAX_CARDS = CFG.maxCards || 3;
  const CONTAINER_CLASS = 'platinum-upsell';

  if (!MAP_URL) return;

  let mapPromise = null;
  const productCache = {};
  let rendering = false;
  let renderQueued = false;
  let giftRemoving = false;   // guards the auto-removal from re-firing mid-flight
  let giftJustRemoved = false;

  const moneyFmt = new Intl.NumberFormat(
    (window.Shopify && Shopify.locale) || 'sl-SI',
    { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR' }
  );

  function getMap() {
    if (!mapPromise) {
      mapPromise = fetch(MAP_URL)
        .then((r) => (r.ok ? r.json() : {}))
        .catch(() => ({}));
    }
    return mapPromise;
  }

  function getProduct(handle) {
    if (!productCache[handle]) {
      productCache[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    }
    return productCache[handle];
  }

  async function fetchCart() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      return await r.json();
    } catch (e) {
      return { items: [] };
    }
  }

  /**
   * Pick candidate handles from the map for the current cart.
   * Map format v2: { src: { pet: 'dog'|'cat'|null, targets: [...] } } — v1 arrays tolerated.
   * Also collects the pet types of matched cart items so the heading can address
   * the right animal ("Tvoj pes …" / "Tvoja muca …").
   */
  function pickCandidates(map, cart) {
    const inCart = new Set((cart.items || []).map((i) => i.handle));
    const seen = new Set();
    const out = [];
    const petTypes = new Set();
    for (const item of cart.items || []) {
      const entry = map[item.handle];
      if (!entry) continue;
      const targets = Array.isArray(entry) ? entry : entry.targets || [];
      if (!Array.isArray(entry) && entry.pet) petTypes.add(entry.pet);
      for (const t of targets) {
        if (inCart.has(t.handle) || seen.has(t.handle)) continue;
        seen.add(t.handle);
        out.push(t);
      }
    }
    return { targets: out, petTypes };
  }

  /** Heading addressing the right animal; generic for mixed/unknown carts. */
  function pickHeading(petTypes) {
    if (petTypes.size === 1) {
      const pet = petTypes.values().next().value;
      if (pet === 'dog' && CFG.headingDog) return CFG.headingDog;
      if (pet === 'cat' && CFG.headingCat) return CFG.headingCat;
    }
    return HEADING;
  }

  function imageUrl(src, width) {
    if (!src) return '';
    return src + (src.includes('?') ? '&' : '?') + 'width=' + width;
  }

  /** Replicate the theme's native add-to-cart flow (theme.js ProductForm). */
  async function addVariant(variantId, button, source) {
    source = source || 'cart-drawer';
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );
      const body = new FormData();
      body.set('id', String(variantId));
      body.set('quantity', '1');
      // hidden attribution property (underscore prefix = not shown to customer,
      // Prestige line-item.liquid skips it) — lands on the order line item, so
      // upsell-attributed orders/revenue are measurable in Shopify/Supabase
      body.set('properties[_platinum_upsell]', source);
      body.set('sections', sectionsToBundle.join(','));
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
      });
      const responseJson = await response.json();
      if (!response.ok) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }
      // GA4: gtag is defined globally at runtime by the Google & YouTube channel
      // pixel (lax mode) — same defensive pattern as the PFF quiz (proven to land
      // in GA4: quiz_* events visible in reports)
      try {
        if (typeof gtag === 'function') {
          gtag('event', source === 'cart-drawer' ? 'upsell_add' : 'gift_add', {
            event_category: 'PLATINUM Upsell',
            source: source,
            variant_id: String(variantId),
          });
        }
      } catch (e) {}
      // custom event for Shopify Custom Pixels (Settings -> Customer events) —
      // a pixel subscribing to 'platinum_upsell_add' can forward it elsewhere
      try {
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish('platinum_upsell_add', {
            variantId,
            source: source,
          });
        }
      } catch (e) {}
      // cart PAGE has no live re-render in Prestige (theme reloads on change) — mirror that
      if (document.querySelector('.cart-page')) {
        window.location.reload();
        return;
      }
      const cartContent = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      cartContent['sections'] = responseJson['sections'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent },
        })
      );
    } catch (e) {
      console.warn('[Platinum Upsell] add failed:', e);
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  function buildCard(target, product) {
    const variants = (product.variants || []).filter((v) => v.available);
    if (!variants.length) return null;
    // default to the cheapest available variant (lowest entry barrier in the drawer)
    let variant = variants.reduce((min, v) => (v.price < min.price ? v : min), variants[0]);

    // Horizontal card: image | (title / pitch / variant+price) | add button
    const card = document.createElement('div');
    card.className = 'platinum-upsell__card';

    const link = document.createElement('a');
    link.href = product.url || '/products/' + product.handle;
    link.className = 'platinum-upsell__media';
    const img = document.createElement('img');
    img.src = imageUrl(product.featured_image, 240);
    img.alt = product.title;
    img.loading = 'lazy';
    link.appendChild(img);
    card.appendChild(link);

    const title = document.createElement('a');
    title.href = link.href;
    title.className = 'platinum-upsell__title';
    // drop the category suffix ("MeatCrisp Adult Fish, Suha Hrana za Mačke" -> "MeatCrisp Adult Fish")
    title.textContent = product.title.split(',')[0].trim();
    title.title = product.title;
    card.appendChild(title);

    const pitch = document.createElement('p');
    pitch.className = 'platinum-upsell__pitch';
    pitch.textContent = target.pitch || target.reason || '';
    card.appendChild(pitch);

    const options = document.createElement('div');
    options.className = 'platinum-upsell__options';
    const hasRealVariants = variants.length > 1 && variants[0].title !== 'Default Title';
    if (hasRealVariants) {
      const select = document.createElement('select');
      select.className = 'platinum-upsell__select';
      select.setAttribute('aria-label', 'Izberi velikost');
      for (const v of variants) {
        const opt = document.createElement('option');
        opt.value = String(v.id);
        opt.textContent = v.title;
        select.appendChild(opt);
      }
      select.value = String(variant.id);
      select.addEventListener('change', () => {
        variant = variants.find((v) => String(v.id) === select.value) || variant;
        priceValue.textContent = moneyFmt.format(variant.price / 100);
      });
      options.appendChild(select);
    } else if (variant.title && variant.title !== 'Default Title') {
      const size = document.createElement('span');
      size.className = 'platinum-upsell__variant';
      size.textContent = variant.title;
      options.appendChild(size);
    }
    // variant choice + price share one row (horizontal card layout)
    const row = document.createElement('div');
    row.className = 'platinum-upsell__row';
    if (options.childNodes.length) row.appendChild(options);

    const price = document.createElement('div');
    price.className = 'platinum-upsell__price';
    const priceValue = document.createElement('span');
    priceValue.textContent = moneyFmt.format(variant.price / 100);
    price.appendChild(priceValue);
    row.appendChild(price);
    card.appendChild(row);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'platinum-upsell__add';
    btn.textContent = 'Dodaj';
    btn.addEventListener('click', () => addVariant(variant.id, btn));
    card.appendChild(btn);

    return card;
  }

  /**
   * Gift block ("2× MENU 185 g gratis"). The Shopify automatic discount does the
   * zeroing — this block only lets the customer pick flavours and put them in the
   * cart, because Shopify never adds a free product by itself.
   *
   * The threshold is measured on NON-gift lines at their pre-discount price, so
   * the block does not flip off the moment the 5 € discount lands and lowers the
   * subtotal. That also keeps our condition stricter than Shopify's, never looser
   * — we can't promise a gift the discount would refuse.
   */
  /** MENU is dog food — never dangle it in front of a cat-only cart. */
  function isCatOnly(cart, map) {
    const items = cart.items || [];
    if (!items.length) return false;
    return items.every((it) => {
      const entry = map && map[it.handle];
      const pet = entry && !Array.isArray(entry) ? entry.pet : null;
      if (pet) return pet === 'cat';
      return /ma[čc]k/i.test((it.product_type || '') + ' ' + (it.product_title || it.title || ''));
    });
  }

  async function giftState(cart, map) {
    const G = CFG.gift;
    if (!G || !G.enabled || !Array.isArray(G.variants) || !G.variants.length) return null;
    // promo over — stop promising a gift the code will refuse
    if (G.endsAt && Date.now() > Date.parse(G.endsAt)) return null;
    if (!(cart.items || []).length) return null;
    if (isCatOnly(cart, map)) return null;

    const ids = new Set(G.variants.map((v) => Number(v.id)));
    let base = 0;
    let inCart = 0;
    const chosen = [];
    for (const item of cart.items || []) {
      if (ids.has(Number(item.variant_id))) {
        inCart += item.quantity;
        if (isGiftLine(item)) {
          const cfgV = G.variants.find((v) => Number(v.id) === Number(item.variant_id));
          for (let n = 0; n < item.quantity; n++) chosen.push(cfgV ? cfgV.title : item.variant_title);
        }
      } else {
        // What the customer actually PAYS, not the list price. Prestige's free
        // shipping bar counts the same way, so the two thresholds can never
        // disagree in the same drawer (e.g. 30 € list − Appstle bundle = 27 €).
        base += item.final_line_price;
      }
    }
    const missing = Number(G.minSubtotal || 0) - base / 100;

    // Dropped back under the threshold with our gift still in the cart: take it
    // out again. Only lines WE added are removed (property check), so a customer
    // who deliberately bought MENU tins keeps them.
    if (missing > 0.005 && chosen.length > 0) {
      if (!giftRemoving) {
        giftRemoving = true;
        giftJustRemoved = true;
        removeGiftLines(ids).finally(() => { giftRemoving = false; });
      }
      return { cfg: G, mode: 'removed' };
    }

    if (missing > 0.005) {
      giftJustRemoved = false;
      return { cfg: G, mode: 'teaser', missing: missing };
    }
    giftJustRemoved = false;

    // Are the gift lines ACTUALLY discounted? We check the line allocations, not
    // the discount's name: that works for an automatic discount and for a code
    // alike, and it answers the only question that matters — is the tin free?
    // A code can be refused (one use per customer, usage limit, expiry) and the
    // customer would otherwise silently pay for a "gift".
    const codeApplied = (cart.items || []).some(
      (i) => ids.has(Number(i.variant_id)) && (i.line_level_discount_allocations || []).length > 0
    );

    // Live stock check — a sold-out flavour must never be offered as a gift.
    // While we have the product anyway, take its image and URL: a flavour name
    // in a dropdown means nothing to someone who doesn't know the MENU line.
    const available = [];
    for (const v of G.variants) {
      const product = v.handle ? await getProduct(v.handle) : null;
      const match = product && (product.variants || []).find((pv) => Number(pv.id) === Number(v.id));
      if (product && match && !match.available) continue;
      available.push({
        id: v.id,
        handle: v.handle,
        title: v.title,
        name: v.name || (product ? product.title.split(',')[0].trim() : v.title),
        desc: v.desc || '',
        productTitle: product ? product.title.split(',')[0].trim() : v.title,
        image: (match && match.featured_image && match.featured_image.url) || (product && product.featured_image) || '',
        url: product ? product.url || '/products/' + v.handle : '/products/' + v.handle,
      });
    }
    if (!available.length) return null;

    const slots = Number(G.slots || 2);
    const remaining = Math.max(0, slots - inCart);
    let mode = 'pick';
    if (inCart > 0 && !codeApplied) mode = 'refused';
    else if (remaining === 0) mode = 'done';
    return { cfg: G, mode, variants: available, slots, inCart, codeApplied, remaining, chosen };
  }

  /**
   * Apply the promo code to the cart. `?discount=` in a storefront URL is NOT
   * auto-applied — only /discount/<CODE> sets it on the cart session (same
   * finding as the Vzorci silent-add work). Then ask the theme to re-read the
   * cart so the discount line and totals appear in the drawer.
   */
  /**
   * Take the gift lines back out. Used when the code was refused (one use per
   * customer) — the customer should not have to hunt for the lines they were
   * told would be free.
   */
  /** A tin WE put in the cart as a gift — never touch tins the customer chose. */
  function isGiftLine(item) {
    return !!(item.properties && item.properties._platinum_upsell === 'cart-gift');
  }

  async function removeGiftLines(ids, button) {
    if (button) button.disabled = true;
    try {
      let cart = await fetchCart();
      const keys = (cart.items || []).filter((i) => ids.has(Number(i.variant_id)) && isGiftLine(i)).map((i) => i.key);
      for (const key of keys) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
      }
    } catch (e) {
      console.warn('[Platinum Upsell] gift removal failed:', e);
    } finally {
      if (button) button.disabled = false;
      if (document.querySelector('.cart-page')) window.location.reload();
      else document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  async function applyDiscountCode(code, silent) {
    try {
      await fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' });
    } catch (e) {
      console.warn('[Platinum Upsell] discount apply failed:', e);
    }
    if (!silent) document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function buildGiftBlock(state) {
    const G = state.cfg;
    const box = document.createElement('div');
    box.className = 'platinum-upsell__gift';
    const ids = new Set(G.variants.map((v) => Number(v.id)));

    const heading = document.createElement('p');
    heading.className = 'platinum-upsell__gift-heading';
    heading.textContent = (
      (state.mode === 'teaser' && G.teaserHeading) ||
      (state.mode === 'done' && G.doneHeading) ||
      G.heading ||
      'Darilo'
    ).replace('{x}', state.missing ? moneyFmt.format(state.missing) : '');
    box.appendChild(heading);

    const say = (cls, text) => {
      const p = document.createElement('p');
      p.className = cls;
      p.textContent = text;
      box.appendChild(p);
      return p;
    };

    // 1 — under the threshold
    if (state.mode === 'teaser') {
      say(
        'platinum-upsell__gift-teaser',
        (G.teaserText || 'Do darila ti manjka še {x}.').replace('{x}', moneyFmt.format(state.missing))
      );
      return box;
    }

    // 4 — cart fell back under the threshold, gift taken out again
    if (state.mode === 'removed') {
      say('platinum-upsell__gift-warn', G.removedText || 'Darilo smo odstranili — košarica je spet pod pragom.');
      return box;
    }

    // code refused (already used — one per customer)
    if (state.mode === 'refused') {
      say('platinum-upsell__gift-warn', G.failText || 'Darila ni bilo mogoče pripeti.');
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'platinum-upsell__gift-remove';
      remove.textContent = G.removeText || 'Odstrani iz košarice';
      remove.addEventListener('click', () => removeGiftLines(ids, remove));
      box.appendChild(remove);
      return box;
    }

    // 3 — gift is in the cart
    if (state.mode === 'done') {
      const line = say('platinum-upsell__gift-done', G.doneText || '');
      if (state.chosen.length) {
        const what = document.createElement('span');
        what.className = 'platinum-upsell__gift-chosen';
        what.textContent = state.chosen.join(' + ');
        line.appendChild(document.createTextNode(' '));
        line.appendChild(what);
      }
      const swap = document.createElement('button');
      swap.type = 'button';
      swap.className = 'platinum-upsell__gift-remove';
      swap.textContent = G.swapText || 'zamenjaj';
      swap.addEventListener('click', () => removeGiftLines(ids, swap));
      box.appendChild(swap);
      return box;
    }

    // 2 — threshold reached, pick the flavours. One button adds them all: the
    // gift is a single decision, not two.
    if (G.descText) say('platinum-upsell__gift-desc', G.descText);

    const selects = [];
    for (let i = 0; i < state.remaining; i++) {
      const slot = document.createElement('div');
      slot.className = 'platinum-upsell__gift-slot';

      // The gift must read as a product, not as a coupon: picture, full name,
      // one line of benefit, price — same anatomy as the upsell cards.
      const thumbLink = document.createElement('a');
      thumbLink.className = 'platinum-upsell__gift-thumb';
      thumbLink.target = '_blank';
      thumbLink.rel = 'noopener';
      const thumbImg = document.createElement('img');
      thumbImg.loading = 'lazy';
      thumbImg.alt = '';
      thumbLink.appendChild(thumbImg);
      slot.appendChild(thumbLink);

      const nameLink = document.createElement('a');
      nameLink.className = 'platinum-upsell__gift-name';
      nameLink.target = '_blank';
      nameLink.rel = 'noopener';
      slot.appendChild(nameLink);

      const line = document.createElement('p');
      line.className = 'platinum-upsell__gift-line';
      slot.appendChild(line);

      const select = document.createElement('select');
      select.className = 'platinum-upsell__select';
      select.setAttribute('aria-label', 'Izberi okus');
      for (const v of state.variants) {
        const opt = document.createElement('option');
        opt.value = String(v.id);
        opt.textContent = v.title;
        select.appendChild(opt);
      }
      // a different flavour per slot, so nobody has to touch the dropdowns
      select.value = String(state.variants[(i + state.inCart) % state.variants.length].id);
      slot.appendChild(select);
      selects.push(select);

      const price = document.createElement('span');
      price.className = 'platinum-upsell__gift-price';
      if (G.unitPrice) {
        const was = document.createElement('s');
        was.textContent = moneyFmt.format(Number(G.unitPrice));
        price.appendChild(was);
      }
      const now = document.createElement('strong');
      now.textContent = G.freeLabel || 'BREZPLAČNO';
      price.appendChild(now);
      slot.appendChild(price);

      const sync = () => {
        const v = state.variants.find((x) => String(x.id) === select.value) || state.variants[0];
        thumbImg.src = imageUrl(v.image, 140);
        thumbImg.alt = v.name;
        thumbLink.href = v.url;
        nameLink.href = v.url;
        nameLink.textContent = v.name;
        line.textContent = v.desc || '';
      };
      sync();
      select.addEventListener('change', sync);

      box.appendChild(slot);
    }

    // Give the gift a number — 'free' without a value still reads as a coupon.
    if (G.unitPrice && G.valueText) {
      say(
        'platinum-upsell__gift-value',
        G.valueText
          .replace('{n}', String(state.remaining))
          .replace('{unit}', moneyFmt.format(Number(G.unitPrice)))
          .replace('{total}', moneyFmt.format(Number(G.unitPrice) * state.remaining))
      );
    }


    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'platinum-upsell__gift-cta';
    btn.textContent = G.buttonText || 'DODAJ DARILO';
    btn.addEventListener('click', async () => {
      // Apply the code BEFORE adding: on the cart page addVariant() reloads the
      // page (Prestige has no live re-render there), so anything after it never runs.
      const onCartPage = !!document.querySelector('.cart-page');
      if (G.discountCode) await applyDiscountCode(G.discountCode, true);
      for (const sel of selects) await addVariant(sel.value, btn, 'cart-gift');
      if (G.discountCode && !onCartPage) await applyDiscountCode(G.discountCode);
    });
    box.appendChild(btn);

    if (state.inCart > 0) {
      say('platinum-upsell__gift-note', 'Dodano ' + state.inCart + ' od ' + state.slots + '.');
    }

    return box;
  }

  /**
   * Safety net right above the checkout button: the threshold is reached but the
   * gift is still unclaimed. The customer was promised it in an email — do not
   * let them check out past it without noticing.
   */
  function renderCheckoutGuard(text) {
    const buttons = document.querySelectorAll(
      '#cart-drawer [name="checkout"], .cart-drawer [name="checkout"], .cart-page [name="checkout"]'
    );
    buttons.forEach((btn) => {
      const host = btn.parentElement;
      if (!host) return;
      let el = host.querySelector(':scope > .platinum-upsell__guard');
      if (!text) {
        if (el) el.remove();
        return;
      }
      if (!el) {
        el = document.createElement('p');
        el.className = 'platinum-upsell__guard';
        host.insertBefore(el, btn);
      }
      el.textContent = text;
    });
  }

  /**
   * Injection points: cart drawer (append after line items) and cart page
   * (insert above .cart-footer). Both can exist on /cart — each gets its own
   * container instance.
   */
  function getPlacements() {
    const out = [];
    const drawerItems = document.querySelector('#cart-drawer .cart-drawer__items');
    if (drawerItems) out.push({ host: drawerItems, ref: null, modifier: '' });
    const cartFooter = document.querySelector('.cart-page .cart-footer');
    if (cartFooter) out.push({ host: cartFooter.parentElement, ref: cartFooter, modifier: 'platinum-upsell--page' });
    return out;
  }

  /**
   * Render (or remove) the upsell block for the given cart.
   * Re-entrant calls are QUEUED, not dropped — the drawer wipes its content
   * (incl. our container) asynchronously after cart:change, so a re-render
   * request can arrive while one is in flight; dropping it would leave the
   * drawer without the upsell block (bug found in QA 2026-06-12).
   */
  async function render(cart) {
    if (rendering) {
      renderQueued = true;
      return;
    }
    rendering = true;
    try {
      const placements = getPlacements();
      if (!placements.length) return;

      if (!cart) cart = await fetchCart();

      let pairs = [];
      let headingText = HEADING;
      let gift = null;
      if (cart.items && cart.items.length > 0) {
        const map = await getMap();
        gift = await giftState(cart, map);
        // while the promo runs the gift flavours must not also show up as paid
        // cards — "GRATIS" and "2,50 €" for the same tin, one under the other
        const giftHandles = new Set(gift ? (gift.cfg.variants || []).map((v) => v.handle) : []);
        const picked = pickCandidates(map, cart);
        headingText = pickHeading(picked.petTypes);
        for (const t of picked.targets) {
          if (pairs.length >= MAX_CARDS) break;
          if (giftHandles.has(t.handle)) continue;
          const product = await getProduct(t.handle);
          if (!product || !product.available) continue;
          pairs.push([t, product]);
        }
      }

      renderCheckoutGuard(gift && gift.mode === 'pick' ? gift.cfg.guardText || '' : '');

      for (const place of placements) {
        const existing = place.host.querySelector(':scope > .' + CONTAINER_CLASS);
        if (!pairs.length && !gift) {
          if (existing) existing.remove();
          continue;
        }
        const container = existing || document.createElement('div');
        container.className = (CONTAINER_CLASS + ' ' + place.modifier).trim();
        container.innerHTML = '';

        // gift first — it is the stronger message, and it expires with the promo
        if (gift) container.appendChild(buildGiftBlock(gift));

        if (pairs.length) {
          const heading = document.createElement('p');
          heading.className = 'platinum-upsell__heading';
          heading.textContent = headingText;
          container.appendChild(heading);

          const grid = document.createElement('div');
          grid.className = 'platinum-upsell__grid';
          for (const [t, product] of pairs) {
            const card = buildCard(t, product);
            if (card) grid.appendChild(card);
          }
          container.appendChild(grid);
        }

        if (!existing) {
          if (place.ref) place.host.insertBefore(container, place.ref);
          else place.host.appendChild(container);
        }
      }
    } catch (e) {
      console.warn('[Platinum Upsell] render failed:', e);
    } finally {
      rendering = false;
      if (renderQueued) {
        renderQueued = false;
        render(); // re-run with fresh cart — a newer state arrived mid-render
      }
    }
  }

  function init() {
    render();
    // Cart changed (native or ours): re-render with fresh cart data.
    document.addEventListener('cart:change', (event) => {
      render(event.detail && event.detail.cart);
    });
    // Drawer replaced its whole content (wipes our container) — re-inject.
    document.addEventListener('cart-drawer:refreshed', () => render());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
